import { default as Box } from '@mui/material/Box';
import { alpha, useTheme } from '@mui/material/styles';
import { default as Typography } from '@mui/material/Typography';
import { AppButton } from 'components/AppButton';
import { IMAGES } from 'config/images';
import { getTrans } from 'modules/i18n';
import {
  defaultPreferencesDisabled,
  defaultPreferencesEnabled
} from '../constants';
import { useBraze } from '../hooks';
import { usePreferencesMutation, usePreferencesQuery } from '../model/query';

interface Props {
  close: () => void;
}

export const NotificationPrompt = ({ close }: Props) => {
  const braze = useBraze();
  const theme = useTheme();

  const { data } = usePreferencesQuery();
  const preferencesMutation = usePreferencesMutation();

  if (!braze || !data) return null;

  const textColor = theme.palette.primary.main;

  const updatePreferences = () => {
    const permission =
      'Notification' in window && Notification.permission
        ? Notification.permission
        : null;

    if (permission === 'granted') {
      preferencesMutation.mutate({
        ...defaultPreferencesEnabled,
        pushPermission: true,
        refId: data.data.notifications.refId
      });
    } else if (permission === 'denied') {
      preferencesMutation.mutate({
        ...defaultPreferencesDisabled,
        pushPermission: false,
        refId: data.data.notifications.refId
      });
    }
  };

  const getPermission = () => {
    braze.requestPushPermission(
      // allowed
      () => {
        console.info('[Braze Debug] Push permission granted');
        updatePreferences();
        close();
      },
      // denied or error
      () => {
        console.info('[Braze Debug] Push permission denied');
        updatePreferences();
        close();
      }
    );
  };

  return (
    <Box>
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'center',
          marginBottom: '20px'
        }}
      >
        <img
          alt={'ZENJOB_PURPLE_LOGO_ALT_TEXT'}
          src={IMAGES.proLogoPurple}
          style={{
            height: '11vh',
            marginBottom: '10px',
            marginTop: '5vh'
          }}
        />
      </Box>
      <Box
        sx={{
          backgroundColor: alpha(theme.palette.primary.lighter, 0.2),
          padding: '32px'
        }}
      >
        <Typography variant="h1" color={textColor} mb={3}>
          {getTrans('push.prompt.header')}
        </Typography>
        <Typography variant="subtitle1" color={textColor} mb={2}>
          {getTrans('push.prompt.p1')}
        </Typography>
        <Typography variant="subtitle1" color={textColor}>
          {getTrans('push.prompt.p2')}
        </Typography>
      </Box>
      <Box
        sx={{
          padding: '0 32px 32px',
          position: 'absolute',
          left: 0,
          right: 0,
          bottom: 0
        }}
      >
        <AppButton
          color="secondary"
          fullWidth
          sx={{ mb: 3 }}
          onClick={getPermission}
        >
          {getTrans('push.prompt.allow')}
        </AppButton>
        <AppButton variant="outlined" fullWidth onClick={close}>
          {getTrans('push.prompt.dismiss')}
        </AppButton>
      </Box>
    </Box>
  );
};
